import { useCallback, useEffect, useState, useRef, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Icon, Typography } from 'shared/ui'
import { isLink, logger } from 'shared/lib'
import { makeTextFieldAutoFocus, TextField } from 'shared/ui/TextField'
import { type ShortLinkStore } from 'entities/ShortLink/store/shortLinkStore'
import styles from './styles.module.scss'

type IShortLinkContentProps = {
  store: ShortLinkStore
}

export const ShortLinkContent: FC<IShortLinkContentProps> = observer(({ store }) => {
  const [link, setLink] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const { editLink, createShortLink, handleShortenLink, handleAddShortLink, handleSaveLink } = store

  const inputRef = useRef<HTMLInputElement>(null)
  const focusPositionRef = useRef<number | null>(null)
  const isAddDisabled = !link.trim() || loading || !store.canShortUrl(link)

  const onChangeValue = (value: string) => {
    setError(false)
    setLink(value)
  }

  const handleInsertMergeFieldValue = useCallback((value: string) => {
    const encoded = encodeURIComponent(value)
    const position = inputRef.current?.selectionStart ?? 0

    setError(false)
    setLink((previousLink) => {
      const pos = Math.min(position, previousLink.length)
      focusPositionRef.current = pos + encoded.length

      return previousLink.slice(0, pos) + encoded + previousLink.slice(pos)
    })
  }, [])

  useEffect(() => {
    if (focusPositionRef.current !== null) {
      const input = inputRef.current
      const cursor = focusPositionRef.current
      focusPositionRef.current = null

      if (input) {
        input.focus()
        input.setSelectionRange(cursor, cursor)
      }
    }
  }, [link])

  useEffect(() => {
    if (inputRef.current && editLink) {
      const end = editLink.href.length
      inputRef.current.setSelectionRange(end, end)
      inputRef.current.focus()
    }
  }, [editLink])

  useEffect(() => {
    if (editLink) {
      setLink(editLink.href)
    }
  }, [editLink])

  const onAdd = async () => {
    if (!isLink(link)) {
      setError(true)
      return
    }
    setError(false)
    try {
      const shortLink = await createShortLink(link)
      if (shortLink) {
        if (editLink) {
          handleShortenLink(shortLink)
        } else {
          handleAddShortLink(shortLink)
        }
      }
    } catch (e) {
      logger.error(e)
    } finally {
      setLoading(false)
    }
  }

  const onSave = () => {
    if (!isLink(link)) {
      setError(true)
      return
    }
    setError(false)
    handleSaveLink(link)
  }

  return (
    <div className={styles.wrap}>
      <TextField
        className={styles.textField}
        variant='integrated'
        size='small'
        InputProps={{
          placeholder: 'Enter your URL',
          ref: inputRef,
        }}
        onChange={onChangeValue}
        value={link}
        ariaLabel='LabelShortenUrl'
        mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
      />
      {error && (
        <div className={styles.helper}>
          <Icon
            icon={'alertTriangleSmall'}
            color={'var(--content-negative-primary)'}
            fontSize={16}
          />
          <Typography
            variant={'text-input-helper'}
            color={'negativePrimary'}
            ariaLabel={'Please enter'}
          >
            Please enter a valid URL
          </Typography>
        </div>
      )}
      <div className={styles.actions}>
        {store.mergeFieldAction?.(handleInsertMergeFieldValue)}
        <div className={styles.actionsRight}>
          {editLink ? (
            <>
              <Button
                text={'Shorten URL'}
                size={'medium'}
                contained={'secondary'}
                disabled={isAddDisabled}
                onClick={onAdd}
              />
              <Button
                text={'Save'}
                size={'medium'}
                contained={'primary'}
                disabled={!link.trim()}
                onClick={onSave}
              />
            </>
          ) : (
            <Button
              text={'Add URL'}
              size={'medium'}
              contained={'secondary'}
              disabled={isAddDisabled}
              onClick={onAdd}
            />
          )}
        </div>
      </div>
    </div>
  )
})
